// Web Speech API integration for real-time transcription
export interface WordWithTimestamp {
  word: string;
  startTime: number;
  endTime: number;
  confidence?: number;
}

export interface SpeechRecognitionResult {
  transcript: string;
  confidence: number;
  isFinal: boolean;
  words: string[];
  wordsWithTimestamps?: WordWithTimestamp[];
  currentWordIndex?: number;
}

export interface SpeechRecognitionError {
  error: string;
  message: string;
}

// TypeScript declarations for Web Speech API
interface ISpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface ISpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

export class WebSpeechService {
  private recognition: any;
  private isListening: boolean = false;
  private finalTranscript: string = '';
  private interimTranscript: string = '';
  private allWords: string[] = [];
  private wordsWithTimestamps: WordWithTimestamp[] = [];
  private startTime: number = 0;
  
  constructor() {
    // Check for browser support
    console.log('Checking Web Speech API support...');
    console.log('window.SpeechRecognition:', typeof window.SpeechRecognition);
    console.log('window.webkitSpeechRecognition:', typeof window.webkitSpeechRecognition);
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.warn('Web Speech API not supported in this browser');
      console.log('User Agent:', navigator.userAgent);
      this.recognition = null;
      return;
    }
    
    console.log('Web Speech API found, initializing...');
    this.recognition = new SpeechRecognition();
    this.setupRecognition();
  }
  
  private setupRecognition() {
    if (!this.recognition) return;
    
    // Configure recognition for better accuracy
    this.recognition.continuous = true; // Keep listening until stopped
    this.recognition.interimResults = true; // Get results while speaking
    this.recognition.maxAlternatives = 3; // Get top 3 alternatives for better accuracy
    
    // Detect user's language/accent preference
    const userLang = navigator.language || 'en-US';
    const supportedAccents = [
      'en-US', // American English
      'en-GB', // British English  
      'en-AU', // Australian English
      'en-CA', // Canadian English
      'en-IN', // Indian English
      'en-NZ', // New Zealand English
      'en-ZA', // South African English
    ];
    
    // Use user's locale if supported, otherwise default to en-US
    this.recognition.lang = supportedAccents.includes(userLang) ? userLang : 'en-US';
    
    console.log(`Web Speech API initialized with language: ${this.recognition.lang}`);
    console.log('Max alternatives set to 3 for better accuracy');
  }
  
  // Get current language setting
  getLanguage(): string {
    return this.recognition ? this.recognition.lang : 'en-US';
  }
  
  // Check if Web Speech API is available
  isAvailable(): boolean {
    return this.recognition !== null;
  }
  
  // Start listening
  start(
    onResult: (result: SpeechRecognitionResult) => void,
    onError?: (error: SpeechRecognitionError) => void
  ): boolean {
    if (!this.recognition) {
      console.error('Web Speech API not available');
      if (onError) {
        onError({
          error: 'not-supported',
          message: 'Web Speech API is not supported in this browser. Please use Chrome or Edge.'
        });
      }
      return false;
    }
    
    if (this.isListening) {
      console.warn('Already listening');
      return false;
    }
    
    // Reset transcripts
    this.finalTranscript = '';
    this.interimTranscript = '';
    this.allWords = [];
    this.wordsWithTimestamps = [];
    this.startTime = Date.now();
    
    // Set up event handlers
    this.recognition.onstart = () => {
      this.isListening = true;
      console.log('Web Speech API started listening');
    };
    
    this.recognition.onresult = (event: ISpeechRecognitionEvent) => {
      this.interimTranscript = '';
      const currentTime = Date.now() - this.startTime;
      
      // Process all results
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        
        // Check all alternatives for best confidence
        let bestTranscript = result[0].transcript;
        let bestConfidence = result[0].confidence || 0;
        
        // If we have alternatives, check if any have higher confidence
        for (let j = 1; j < Math.min(result.length, 3); j++) {
          const altConfidence = result[j].confidence || 0;
          if (altConfidence > bestConfidence) {
            bestTranscript = result[j].transcript;
            bestConfidence = altConfidence;
          }
        }
        
        const transcript = bestTranscript;
        
        if (result.isFinal) {
          this.finalTranscript += transcript + ' ';
          // Extract words from final transcript and estimate timestamps
          const words = transcript.trim().split(/\s+/).filter(w => w.length > 0);
          
          // Improved word timestamp estimation based on word length
          let currentWordTime = currentTime - (words.length * 250); // Start time for this batch
          
          words.forEach((word, index) => {
            // Estimate duration based on word length (longer words take more time)
            const wordDuration = Math.max(150, Math.min(500, word.length * 50));
            const wordStart = currentWordTime;
            const wordEnd = wordStart + wordDuration;
            
            this.wordsWithTimestamps.push({
              word,
              startTime: wordStart,
              endTime: wordEnd,
              confidence: bestConfidence
            });
            
            currentWordTime = wordEnd + 50; // Small gap between words
          });
          
          this.allWords.push(...words);
        } else {
          this.interimTranscript += transcript;
        }
      }
      
      // Calculate current word index based on current time
      const currentWordIndex = this.wordsWithTimestamps.findIndex(w => 
        currentTime >= w.startTime && currentTime <= w.endTime
      );
      
      // Send result to callback
      const currentTranscript = this.finalTranscript + this.interimTranscript;
      onResult({
        transcript: currentTranscript.trim(),
        confidence: event.results[event.results.length - 1][0].confidence || 0,
        isFinal: false,
        words: this.allWords,
        wordsWithTimestamps: this.wordsWithTimestamps,
        currentWordIndex: currentWordIndex >= 0 ? currentWordIndex : this.wordsWithTimestamps.length - 1
      });
    };
    
    this.recognition.onerror = (event: ISpeechRecognitionErrorEvent) => {
      console.error('Web Speech API error:', event.error);
      this.isListening = false;
      
      if (onError) {
        onError({
          error: event.error,
          message: event.message || this.getErrorMessage(event.error)
        });
      }
    };
    
    this.recognition.onend = () => {
      this.isListening = false;
      console.log('Web Speech API stopped listening');
      
      // Send final result
      if (this.finalTranscript) {
        onResult({
          transcript: this.finalTranscript.trim(),
          confidence: 1,
          isFinal: true,
          words: this.allWords,
          wordsWithTimestamps: this.wordsWithTimestamps,
          currentWordIndex: this.wordsWithTimestamps.length - 1
        });
      }
    };
    
    // Start recognition
    try {
      this.recognition.start();
      return true;
    } catch (error) {
      console.error('Failed to start recognition:', error);
      if (onError) {
        onError({
          error: 'start-failed',
          message: 'Failed to start speech recognition'
        });
      }
      return false;
    }
  }
  
  // Stop listening
  stop(): void {
    if (!this.recognition || !this.isListening) return;
    
    try {
      this.recognition.stop();
      this.isListening = false;
    } catch (error) {
      console.error('Error stopping recognition:', error);
    }
  }
  
  // Abort listening (stop immediately without processing pending results)
  abort(): void {
    if (!this.recognition || !this.isListening) return;
    
    try {
      this.recognition.abort();
      this.isListening = false;
    } catch (error) {
      console.error('Error aborting recognition:', error);
    }
  }
  
  // Get current listening status
  getIsListening(): boolean {
    return this.isListening;
  }
  
  // Set recognition language
  setLanguage(langCode: string): void {
    if (!this.recognition) return;
    this.recognition.lang = langCode;
    console.log(`Speech recognition language set to: ${langCode}`);
  }
  
  // Get supported languages (common ones)
  getSupportedLanguages(): { code: string; name: string }[] {
    return [
      { code: 'en-US', name: 'English (US)' },
      { code: 'en-GB', name: 'English (UK)' },
      { code: 'en-AU', name: 'English (Australia)' },
      { code: 'en-IN', name: 'English (India)' },
      { code: 'es-ES', name: 'Spanish (Spain)' },
      { code: 'es-MX', name: 'Spanish (Mexico)' },
      { code: 'fr-FR', name: 'French' },
      { code: 'de-DE', name: 'German' },
      { code: 'it-IT', name: 'Italian' },
      { code: 'pt-BR', name: 'Portuguese (Brazil)' },
      { code: 'zh-CN', name: 'Chinese (Mandarin)' },
      { code: 'ja-JP', name: 'Japanese' },
      { code: 'ko-KR', name: 'Korean' },
      { code: 'hi-IN', name: 'Hindi' },
      { code: 'ar-SA', name: 'Arabic' },
      { code: 'ru-RU', name: 'Russian' }
    ];
  }
  
  // Get error message
  private getErrorMessage(error: string): string {
    switch (error) {
      case 'no-speech':
        return 'No speech was detected. Please try again.';
      case 'audio-capture':
        return 'No microphone was found or microphone access was denied.';
      case 'not-allowed':
        return 'Microphone permission was denied. Please allow microphone access.';
      case 'network':
        return 'Network error occurred. Please check your internet connection.';
      case 'aborted':
        return 'Speech recognition was aborted.';
      case 'language-not-supported':
        return 'The selected language is not supported.';
      case 'service-not-allowed':
        return 'Speech recognition service is not allowed. Please use HTTPS.';
      default:
        return `Speech recognition error: ${error}`;
    }
  }
}

// Singleton instance
let webSpeechInstance: WebSpeechService | null = null;

export const getWebSpeechService = (): WebSpeechService => {
  if (!webSpeechInstance) {
    webSpeechInstance = new WebSpeechService();
  }
  return webSpeechInstance;
};